import { NostrPrefix } from "@snort/shared"
import { EventKind, NostrLink, type TaggedNostrEvent } from "@snort/system"
import { useUserProfile } from "@snort/system-react"
import { useCallback, useRef } from "react"
import { useInView } from "react-intersection-observer"
import { FormattedMessage } from "react-intl"
import Icon from "@/Components/Icons/Icon"
import useModeration from "@/Hooks/useModeration"
import { getDisplayName } from "@/Utils"
import NoteQuote from "./Note/NoteQuote"

export interface NoteReactionProps {
  data: TaggedNostrEvent
  root?: TaggedNostrEvent
  depth?: number
}
export default function NoteReaction(props: NoteReactionProps) {
  const { data: ev } = props
  const { isMuted } = useModeration()
  const { inView, ref: inViewRef } = useInView({ triggerOnce: true, rootMargin: "2000px" })
  const rootRef = useRef<HTMLDivElement>(null)
  const profile = useUserProfile(ev.pubkey, rootRef)

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      rootRef.current = el
      inViewRef(el)
    },
    [inViewRef],
  )

  const links = NostrLink.fromTags(ev.tags)
  const refEvent = links.find(a => [NostrPrefix.Event, NostrPrefix.Note, NostrPrefix.Address].includes(a.type))
  const isOpMuted = refEvent?.author && isMuted(refEvent.author)
  if (isOpMuted) {
    return
  }

  function action() {
    switch (ev.kind) {
      case EventKind.Repost: {
        return (
          <>
            <Icon name="repeat" size={18} />
            <FormattedMessage
              defaultMessage="{name} reposted"
              values={{
                name: getDisplayName(profile, ev.pubkey),
              }}
            />
          </>
        )
      }
      case EventKind.Reaction: {
        return (
          <FormattedMessage
            defaultMessage="{name} liked"
            values={{
              name: getDisplayName(profile, ev.pubkey),
            }}
          />
        )
      }
    }
  }

  function inner() {
    return (
      <>
        <div className="flex gap-1 text-base font-semibold px-3 py-2 border-b">{action()}</div>
        {refEvent && (
          <NoteQuote
            link={refEvent}
            className=""
            depth={props.depth}
            options={{
              showFooter: true,
              truncate: true,
            }}
          />
        )}
      </>
    )
  }

  return (
    <div className="flex flex-col gap-2" ref={setRef}>
      {inView && inner()}
    </div>
  )
}
